import type { Type, Entity, Property } from '@cognitoforms/model.js';
import { isEntityType } from '@cognitoforms/model.js';
import { FORM_ENTRY_TYPE_NAME } from 'src/framework/forms-model';

export function hasBaseType(child: Type, parentName: string): boolean {
	let baseType = child.baseType;
	while (baseType) {
		if (baseType.fullName === parentName)
			return true;
		baseType = baseType.baseType;
	}
	return false;
}

export function isDynamicEntity(property: Property) {
	return isEntityType(property.propertyType)
		&& hasBaseType(property.propertyType.meta, 'DynamicEntity');
}

export function isLookupDynamicEntity(property: Property) {
	return isEntityType(property.propertyType)
		&& hasBaseType(property.propertyType.meta, 'LookupDynamicEntity');
}

export function isChoiceField(property: Property): boolean {
	return property.propertyType === String
		&& property.containingType?.getProperty(property.name + '_Choices') != null;
}

/**
 * Determine if the field is a lookup based off property name and type.
 * @param property
 * @returns boolean
 */
export function isLookupField(property: Property): boolean {
	const type = isEntityType(property.propertyType) && property.propertyType.meta;
	return property.name !== 'ParentSection' && property.name !== 'Form' && type
		&& (hasBaseType(type, FORM_ENTRY_TYPE_NAME) || hasBaseType(type, 'LookupDynamicEntity'));
}

/**
 * Recursively visit all model properties of the provided entity.
 */
export function visitEntity(
	entity: Entity,
	callback: (entity: Entity, property: Property, parentProperty: Property | null) => void,
	options: { followCircularProperties: boolean, followLookups: boolean }
) {
	const visited = new Set();

	function visit(entity: Entity, parentProperty: Property = null) {
		if (entity === null || entity === undefined || visited.has(entity))
			return;

		visited.add(entity);

		for (const p of entity.meta.type.properties) {
			// Don't visit circular properties
			if (!options.followCircularProperties && (p.name === 'ParentSection' || (p.name === 'Form' && p.containingType.fullName !== FORM_ENTRY_TYPE_NAME)))
				continue;

			callback(entity, p, parentProperty);

			if (!options.followLookups && isLookupField(p))
				continue;

			if (isEntityType(p.propertyType)) {
				if (p.isList)
					(p.value(entity) as Entity[] || []).forEach(item => visit(item, p));
				else
					visit(p.value(entity), p);
			}
		}
	}

	visit(entity);
}

export function visitType(type: Type, callback: (type: Type, path: string) => void) {
	const visited = new Set();
	let path = '';
	function visit(type: Type) {
		if (type === null || type === undefined || visited.has(type))
			return;

		visited.add(type);
		callback(type, path);

		for (const p of type.properties) {
			if (isEntityType(p.propertyType)) {
				if (path.length)
					path += '.';

				path += p.name;
				visit(p.propertyType.meta);
				path = path.substring(0, path.lastIndexOf('.'));
			}
		}
	}
	visit(type);
}

/**
 * Counts the number of lookup fields for an entry
 * @param entity
 * @returns Count of lookup fields
 */
export function countLookupFields(entity: Entity): number {
	const lookupProperties = new Set<Property>();
	if (entity)
		visitEntity(entity, (_, prop) => {
			if (isLookupField(prop))
				lookupProperties.add(prop);
		}, { followCircularProperties: false, followLookups: false });

	return lookupProperties.size;
}